import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";

export default function Users() {
  const [users, setUsers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [err, setErr] = useState("");

  const refresh = async () => {
    const { data } = await api.get("/admin/users");
    setUsers(data);
  };
  useEffect(() => { refresh(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await api.post("/admin/users", form);
      setShowAdd(false);
      setForm({ email: "", password: "", name: "" });
      refresh();
    } catch (ex) {
      setErr(formatErr(ex.response?.data?.detail));
    }
  };

  const toggleActive = async (u) => {
    await api.patch(`/admin/users/${u.id}`, { active: !u.active });
    refresh();
  };

  const remove = async (u) => {
    if (!confirm(`Delete ${u.email}? This also wipes their vault & history.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      refresh();
    } catch (ex) {
      alert(formatErr(ex.response?.data?.detail));
    }
  };

  return (
    <div className="p-10 max-w-6xl">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs tracking-widest text-emerald-400/80">USERS</div>
          <h1 className="text-2xl font-semibold mt-1">User management</h1>
        </div>
        <button
          data-testid="add-user-btn"
          onClick={() => setShowAdd(true)}
          className="px-4 py-2 rounded-md bg-emerald-500 hover:bg-emerald-400 text-zinc-900 text-sm font-medium"
        >
          + Add user
        </button>
      </div>

      <div className="rounded-lg border border-zinc-800 bg-[#0e0f14] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-zinc-500 text-xs uppercase tracking-wider">
            <tr className="border-b border-zinc-800">
              <th className="text-left p-4">Email</th>
              <th className="text-left p-4">Name</th>
              <th className="text-left p-4">Role</th>
              <th className="text-left p-4">Status</th>
              <th className="text-right p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-zinc-900 last:border-0 hover:bg-zinc-900/40" data-testid={`user-row-${u.email}`}>
                <td className="p-4">
                  <Link to={`/users/${u.id}`} className="text-emerald-300 hover:underline">{u.email}</Link>
                  {u.auth_provider === "gosnd" && (
                    <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300 border border-violet-500/30 align-middle" title="Signed in via emp.gosnd.com SSO">SSO</span>
                  )}
                </td>
                <td className="p-4 text-zinc-300">
                  <div>{u.name || "—"}</div>
                  {(u.designation || u.department || u.emp_id) && (
                    <div className="text-[11px] text-zinc-500 mt-0.5">
                      {u.emp_id && <span>#{u.emp_id}</span>}
                      {u.emp_id && (u.designation || u.department) && <span> · </span>}
                      {u.designation || u.department}
                    </div>
                  )}
                </td>
                <td className="p-4">
                  <span className={`text-xs px-2 py-0.5 rounded ${u.role === "admin" ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" : "bg-zinc-800 text-zinc-300"}`}>
                    {u.role}
                  </span>
                </td>
                <td className="p-4">
                  <span className={`text-xs ${u.active ? "text-emerald-400" : "text-red-400"}`}>
                    {u.active ? "Active" : "Disabled"}
                  </span>
                </td>
                <td className="p-4 text-right space-x-2">
                  <button
                    data-testid={`toggle-${u.email}`}
                    onClick={() => toggleActive(u)}
                    className="text-xs px-3 py-1 rounded border border-zinc-700 hover:border-zinc-500"
                  >
                    {u.active ? "Disable" : "Enable"}
                  </button>
                  {u.role !== "admin" && (
                    <button
                      onClick={() => remove(u)}
                      className="text-xs px-3 py-1 rounded border border-red-900/60 text-red-400 hover:bg-red-950"
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <form onSubmit={create} className="w-full max-w-md bg-[#0e0f14] border border-zinc-800 rounded-lg p-6 space-y-4">
            <div>
              <h2 className="text-lg font-semibold">Add user</h2>
              <p className="text-xs text-zinc-500">Creates a regular user with default lockdown policy.</p>
            </div>
            <div>
              <label className="text-xs text-zinc-400">Email</label>
              <input data-testid="new-user-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-sm" />
            </div>
            <div>
              <label className="text-xs text-zinc-400">Name</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-sm" />
            </div>
            <div>
              <label className="text-xs text-zinc-400">Password</label>
              <input data-testid="new-user-password" required type="text" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-sm" />
            </div>
            {err && <div className="text-sm text-red-400">{err}</div>}
            <div className="flex gap-3 justify-end">
              <button type="button" onClick={() => setShowAdd(false)} className="px-4 py-2 text-sm rounded-md border border-zinc-800">Cancel</button>
              <button data-testid="new-user-submit" type="submit" className="px-4 py-2 text-sm rounded-md bg-emerald-500 text-zinc-900 font-medium">Create</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
